import { useState, useEffect, useRef } from "react";
import { CustomProperty, FileToCloudURL } from "winslow-core";
import { selectUser } from "../../../redux/features/appData";
import { Button } from "../../../ds2/components";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { apiDeleteEmployeeRecordFileFromCloud, postMultipleFileUpload } from "../../../services/client"
import "./AppCustomPropertyFile.css";
import { AppCustomPropertyError } from "./AppCustomPropertyError";
import { X } from "lucide-react";
import ModalDeleteFileConfirm from "./ModalDeleteFileConfirm";


const MAX_FILE_UPLOAD_SIZE = 100 * 1024 * 1024; // 100MB (increased for video files)

type AppCustomPropertyFileProps = {
    customProperty: CustomProperty;
    data: any;
    disabled: boolean;
    validate: boolean;
    onChange: (label: string, data: any) => void;
}

export const AppCustomPropertyFile = ({customProperty, data, disabled, validate, onChange} : AppCustomPropertyFileProps) => {

    const storeUser = useSelector(selectUser)

    const [oldFiles, setOldFiles] = useState<FileToCloudURL[] | null>(data || null);
    const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
    const uploadFileRef = useRef(null);
    
    const [confirmDeleteFileModal, setConfirmDeleteFileModal] = useState<boolean>(false);
    const [confirmDeleteFile, setConfirmDeleteFile] = useState<FileToCloudURL | null>(null);

    const settings = JSON.parse(customProperty.settings || "{}");

    const handleFileChange = (event: any) => {
        if (!storeUser) {
          return;
        }
    
        if (event.target.files && event.target.files?.length < 1) {
          return;
        }

        // make sure they are not too big
        for (let i = 0; i < event.target.files.length; i++) {
          if (event.target.files[i].size > MAX_FILE_UPLOAD_SIZE) {
            toast("File is too big! Please keep it under 20MB", toastFail);
            return;
          }
        }
    
        const filesArray = uploadFiles ? Array.from(uploadFiles) : [];
        if (event.target.files) {
          const newFiles = [
            ...Array.from(event.target.files),
            ...filesArray,
          ] as File[];
          const dataTransfer = new DataTransfer();
          newFiles.forEach((file: File) => dataTransfer.items.add(file as File));
          
          // const newFileList = [...uploadFiles]
          // setUploadFiles(upload)
          setUploadFiles(dataTransfer.files);

           // we don't pass different data back because we don't want to 
          // overwrite existing files
          // onChange(customProperty.label, dataTransfer.files);
          onChange(customProperty.label, {...data, newFiles: dataTransfer.files, oldFiles: oldFiles});
          // onChange(customProperty.label, dataTransfer.files);
        }

        event.target.value = null;
    };
    

    const handleOpenSystemFilePicker = () => {
        if (uploadFileRef?.current) {
          const inputButton = uploadFileRef.current as any;
          inputButton.click();
        }
    };

    const handleRemoveNewFile = (fileName: string) => {
        if (!uploadFiles) return;
        
        const filesArray = Array.from(uploadFiles);
        const filteredFiles = filesArray.filter(file => file.name !== fileName);
        
        if (filteredFiles.length === 0) {
            setUploadFiles(null);
            onChange(customProperty.label, null);
        } else {
            const dataTransfer = new DataTransfer();
            filteredFiles.forEach((file: File) => dataTransfer.items.add(file as File));
            setUploadFiles(dataTransfer.files);

            // we don't pass different data back because we don't want to 
            // overwrite existing files
            // onChange(customProperty.label, dataTransfer.files);
            onChange(customProperty.label, {...data, newFiles: dataTransfer.files, oldFiles: oldFiles});

        }
    };

    const handleRemoveExistingFile = async () => {
      const updatedOldFiles = oldFiles?.filter((file: FileToCloudURL) => file.filePath !== confirmDeleteFile?.filePath);
      const success = await apiDeleteEmployeeRecordFileFromCloud("", confirmDeleteFile?.cloudURL || "");

      onChange(customProperty.label, {...data, oldFiles: updatedOldFiles});
      setOldFiles(updatedOldFiles || [])
      setConfirmDeleteFileModal(false);
    };


    console.log("[AppCustomPropertyFile] data: ", data);
    // console.log("[AddCustomPropertyFile] uploadFiles: ", uploadFiles);

    return (
        <div className="app-custom-property-file">
            {/* <div className="app-custom-property-label">
                {customProperty.label}
            </div> */}
            <div className="app-custom-property-value">
               
              <div style={{display: "flex", flexDirection: "column", gap: "4px"}}>
                {!disabled && 
                  <>
                    <Button
                      variant="secondary"
                      onClick={() => {
                                handleOpenSystemFilePicker();
                              }}>
                      {settings.allowMultiple ? "Upload Files" : "Upload"}
                    </Button>
                  </>
                } 
                
                 <div className="app-custom-property-file-select">
                     <input
                       type="file"
                       multiple={settings.allowMultiple || false}
                       onChange={handleFileChange}
                       onError={(event) => console.log("onError", event)}
                       ref={uploadFileRef}
                       title="Upload Files"
                     />
                   </div>

                {uploadFiles && uploadFiles.length > 0 && 
                  <div className="app-custom-property-file-list">
                    {Array.from(uploadFiles).map((file: File) => {
                      const formatFileSize = (bytes: number) => {
                        if (bytes === 0) return '0 Bytes';
                        const k = 1024;
                        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                        const i = Math.floor(Math.log(bytes) / Math.log(k));
                        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                      };

                       return (
                         <div
                           key={file.name}
                           className="app-custom-property-file-item"
                         >
                           <div className="app-custom-property-file-content">
                             <div className="app-custom-property-file-info">
                               <div className="app-custom-property-file-name">
                                 {file.name} ({formatFileSize(file.size)})
                               </div>
                             </div>
                             <button
                               className="app-custom-property-file-remove"
                               onClick={() => handleRemoveNewFile(file.name)}
                               title="Remove file"
                             >
                               <X size={16} />
                             </button>
                           </div>
                         </div>
                       )
                    })}
                  </div>
                }
        
                {oldFiles && oldFiles.length > 0 && oldFiles.map((file: FileToCloudURL) => {
                  return (
                    <div
                      key={file.filePath}
                      className="app-custom-property-file-item"
                    >
                      <div className="app-custom-property-file-content">
                        <div className="app-custom-property-file-info">
                          <div className="app-custom-property-file-name">
                            {file.filePath}
                          </div>
                        </div>
                        <button
                          className="app-custom-property-file-remove"
                          onClick={() => {
                            setConfirmDeleteFile(file)
                            setConfirmDeleteFileModal(true)
                          }}
                          title="Delete file"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  )
                })}

                <AppCustomPropertyError 
                    flagRequired={validate && !uploadFiles && !oldFiles && customProperty.required}
                />
              </div>
                
            </div>

            <ModalDeleteFileConfirm
              isOpen={confirmDeleteFileModal}
              filePath={confirmDeleteFile?.filePath}
              onClose={() => {
                setConfirmDeleteFileModal(false)
                setConfirmDeleteFile(null)
              }}
              onContinue={() => handleRemoveExistingFile()}
            />
            
        </div>
    )
}
